import { useState, useEffect } from 'react'
import { fmtUsdt, fmtKrw } from '@/utils/currency'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet-async'
import { partnerApiService } from '@/services/partnerApiService'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Users,
  DollarSign,
  TrendingUp,
  Calendar,
  Mail,
  Activity,
  Search,
  BarChart3
} from 'lucide-react'
import { toast } from 'react-hot-toast'

interface ReferredUser {
  id: number
  email: string
  nickname?: string
  created_at: string
  is_active: boolean
  // 백엔드 원본 필드 (2026-07-24 필드 불일치 수정 — partner_earnings/net_loss가 실제 응답)
  partner_earnings?: number
  net_loss?: number
  settlement_mode?: string
  // 화면 표시용 (loadUsers에서 원본 필드로 채움)
  total_earnings: number
  total_losses: number
  balance_usdt?: number
  bonus_usdt?: number
  balance_krw?: number
  bonus_krw?: number
  trade_count: number
}

interface UsersResponse {
  users: ReferredUser[]
  total: number
  page: number
  limit: number
  pages: number
}

const PartnerUsers = () => {
  const { t, i18n } = useTranslation('users')
  const locale = i18n.language === 'ko' ? 'ko-KR' : i18n.language === 'ja' ? 'ja-JP' : i18n.language === 'zh' ? 'zh-CN' : 'en-US'
  const [users, setUsers] = useState<ReferredUser[]>([])
  const [loading, setLoading] = useState(true)
  const [initialLoad, setInitialLoad] = useState(true)   // 최초 1회만 전체 스피너 (검색 시 리마운트→포커스 소실 방지)
  const [searchInput, setSearchInput] = useState('')      // 입력 즉시 반영(포커스 유지)
  const [search, setSearch] = useState('')                // 디바운스된 실제 쿼리
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalUsers, setTotalUsers] = useState(0)
  const [isMobile, setIsMobile] = useState(false)

  // 검색 디바운스 (300ms) — 매 글자 요청/리마운트 제거
  useEffect(() => {
    const h = setTimeout(() => {
      setSearch(prev => {
        if (prev !== searchInput) setCurrentPage(1)
        return searchInput
      })
    }, 300)
    return () => clearTimeout(h)
  }, [searchInput])

  useEffect(() => {
    loadUsers()
  }, [currentPage, search])

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640)
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const loadUsers = async () => {
    try {
      setLoading(true)
      
      console.log('🔍 PartnerUsers: 사용자 목록 요청 시작', { currentPage })
      const data = await partnerApiService.getUsers(currentPage, 20, search || undefined)
      console.log('✅ PartnerUsers: 사용자 목록 가져오기 성공', data)

      // 백엔드 응답 필드 매핑 — 내 수익=partner_earnings, 유저 손실=net_loss (2026-07-24 수정:
      // 기존엔 응답에 없는 total_earnings/total_losses를 읽어 전부 $0으로 표시되던 버그)
      const usersWithDefaults = (data.users || []).map(user => ({
        ...user,
        total_earnings: (user as any).partner_earnings ?? user.total_earnings ?? 0,
        total_losses: (user as any).net_loss ?? user.total_losses ?? 0,
        trade_count: user.trade_count ?? 0
      }))
      setUsers(usersWithDefaults)
      setTotalPages(data.pages)
      setTotalUsers(data.total)
    } catch (error) {
      console.error('Error loading users:', error)
      toast.error(t('loadError'))
    } finally {
      setLoading(false)
      setInitialLoad(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  }

  if (initialLoad) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner w-8 h-8" />
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <Helmet>
        <title>{t('pageTitle')}</title>
      </Helmet>

      {/* 헤더 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center space-x-2">
            <Users className="w-6 h-6" />
            <span>{t('title')}</span>
          </h1>
          <p className="text-dark-400 mt-1">{t('subtitle')}</p>
        </div>
      </motion.div>

      {/* 통계 요약 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6"
      >
        <div className="glass-card p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-dark-400 text-xs sm:text-sm">{t('totalReferred')}</p>
              <p className="text-xl sm:text-2xl font-bold text-white">{totalUsers}</p>
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400" />
            </div>
          </div>
        </div>

        <div className="glass-card p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-dark-400 text-xs sm:text-sm">{t('totalEarnings')}</p>
              <p className={`text-lg sm:text-2xl font-bold ${users.reduce((sum, user) => sum + user.total_earnings, 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                <span className="sm:hidden">
                  {fmtUsdt(users.reduce((sum, user) => sum + user.total_earnings, 0), 0)}
                </span>
                <span className="hidden sm:inline">
                  {fmtUsdt(users.reduce((sum, user) => sum + user.total_earnings, 0))}
                </span>
              </p>
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
              <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-green-400" />
            </div>
          </div>
        </div>

        <div className="glass-card p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-dark-400 text-xs sm:text-sm">{t('totalTrades')}</p>
              <p className="text-xl sm:text-2xl font-bold text-primary-400">
                {users.reduce((sum, user) => sum + user.trade_count, 0)}
              </p>
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary-500/20 rounded-xl flex items-center justify-center">
              <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-primary-400" />
            </div>
          </div>
        </div>
      </motion.div>

      {/* 사용자 목록 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="glass-card"
      >
        <div className="p-6 border-b border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h3 className="text-lg font-semibold text-white">{t('userList')}</h3>
          {/* 검색 입력 (2026-07-24 — state만 있고 입력 UI가 없어 검색 불가였던 것 수정) */}
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400 pointer-events-none" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('searchPlaceholder')}
              className="w-full pl-9 pr-3 py-2 bg-dark-800/60 border border-white/10 rounded-lg text-sm text-white placeholder-dark-500 focus:outline-none focus:border-primary-500/60"
            />
            {loading && <div className="spinner w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2" />}
          </div>
        </div>

        <div className="p-6">
          {users.length > 0 ? (
            <div className="space-y-4">
              {users.map((user, index) => (
                <motion.div
                  key={user.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="glass-dark p-4 rounded-lg"
                >
                  {/* 모바일 레이아웃 */}
                  <div className="block sm:hidden">
                    <div className="flex items-start space-x-3 mb-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-medium text-sm">
                          {user.email.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-white font-medium text-sm truncate">{user.email}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            user.is_active
                              ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                              : 'bg-red-500/20 text-red-400 border border-red-500/30'
                          }`}>
                            {user.is_active ? t('common:status.active') : t('common:status.inactive')}
                          </span>
                          <div className="flex items-center space-x-1 text-xs text-dark-400">
                            <Calendar className="w-3 h-3" />
                            <span>{formatDate(user.created_at)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-white/10">
                      <div className="text-center">
                        <p className="text-xs text-dark-400 mb-1">{t('myEarnings')}</p>
                        <p className={`text-sm font-semibold ${user.total_earnings >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {fmtUsdt(user.total_earnings, 0)}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-dark-400 mb-1">{t('userLoss')}</p>
                        <p className={`text-sm font-semibold ${user.total_losses >= 0 ? 'text-red-400' : 'text-green-400'}`}>
                          {fmtUsdt(user.total_losses, 0)}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-dark-400 mb-1">{t('tradeCount')}</p>
                        <p className="text-sm font-semibold text-white">
                          {user.trade_count}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/10 text-xs">
                      <span className="text-dark-400">{t('balanceLabel')}</span>
                      <span className="text-white tabular-nums">
                        {fmtUsdt(user.balance_usdt ?? 0)} <span className="text-dark-500">·</span> {fmtKrw(user.balance_krw ?? 0)}
                      </span>
                    </div>
                  </div>

                  {/* 데스크톱 레이아웃 */}
                  <div className="hidden sm:flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                        <span className="text-white font-medium text-sm">
                          {user.email.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="text-white font-medium">{user.email}</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            user.is_active
                              ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                              : 'bg-red-500/20 text-red-400 border border-red-500/30'
                          }`}>
                            {user.is_active ? t('common:status.active') : t('common:status.inactive')}
                          </span>
                        </div>
                        <div className="flex items-center space-x-1 text-sm text-dark-400">
                          <Calendar className="w-3 h-3" />
                          <span>{t('joinDate', { date: formatDate(user.created_at) })}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-4 gap-6 text-right">
                      <div>
                        <p className="text-sm text-dark-400">{t('balanceUsdtKrw')}</p>
                        <p className="text-sm font-semibold text-white tabular-nums">{fmtUsdt(user.balance_usdt ?? 0)}</p>
                        <p className="text-sm font-semibold text-amber-300 tabular-nums">{fmtKrw(user.balance_krw ?? 0)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-dark-400">{t('myEarnings')}</p>
                        <p className={`text-lg font-semibold ${user.total_earnings >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {fmtUsdt(user.total_earnings)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-dark-400">{t('userLoss')}</p>
                        <p className={`text-lg font-semibold ${user.total_losses >= 0 ? 'text-red-400' : 'text-green-400'}`}>
                          {fmtUsdt(user.total_losses)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-dark-400">{t('tradeCount')}</p>
                        <p className="text-lg font-semibold text-white">
                          {t('tradeCountUnit', { count: user.trade_count })}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-dark-600 mx-auto mb-4" />
              <p className="text-dark-400 mb-2">{t('noReferred')}</p>
              <p className="text-sm text-dark-500">{t('shareReferralLink')}</p>
            </div>
          )}
        </div>
      </motion.div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex justify-center"
        >
          <div className="flex items-center space-x-1 sm:space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="glass-button px-2 py-1 sm:px-3 sm:py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common:previous')}
            </button>
            
            {Array.from({ length: Math.min(isMobile ? 3 : 5, totalPages) }, (_, i) => {
              const maxVisible = isMobile ? 3 : 5
              const pageNum = Math.max(1, Math.min(totalPages - (maxVisible - 1), currentPage - Math.floor(maxVisible / 2))) + i
              if (pageNum > totalPages) return null
              
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-2 py-1 sm:px-3 sm:py-2 text-sm rounded-lg transition-colors ${
                    currentPage === pageNum
                      ? 'bg-primary-500 text-white'
                      : 'glass-button'
                  }`}
                >
                  {pageNum}
                </button>
              )
            })}
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="glass-button px-2 py-1 sm:px-3 sm:py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common:next')}
            </button>
          </div>
        </motion.div>
      )}
    </div>
  )
}

export default PartnerUsers